'use client'

import { cn } from '@brew/ui/cn'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@brew/ui/sheet'
import { SignInButton, SignUpButton, useAuth } from '@clerk/nextjs'
import { ChevronDown, ExternalLink, Menu, X } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import {
  BrewAnimatedLogo,
  BrewStaticWordmark,
} from '@/components/icons/brew-animated-logo'
import { homeButtonClasses } from '@/components/marketing/home/home-button'
import { MK_DETAILS_ACCORDION } from '@/components/marketing/home/home-layout'
import { useMediaQuery } from '@/hooks/use-media-query'
import {
  DropdownNavigation,
  type DropdownNavLink,
  type DropdownNavMenu,
} from './dropdown-navigation'
import {
  MARKETING_NAV_CANVAS_BG,
  MARKETING_NAV_CANVAS_SHADOW,
  MARKETING_NAV_HEADER_MAX_SCROLLED,
  MARKETING_NAV_HEADER_MAX_TOP,
  MARKETING_NAV_MOBILE_MARGIN_SCROLLED,
  MARKETING_NAV_MOBILE_MARGIN_TOP,
  MARKETING_NAV_SCROLL_THRESHOLD,
  MARKETING_NAV_SCROLLED_BG,
  MARKETING_NAV_SCROLLED_SHADOW,
  MARKETING_NAV_TOP_BG,
  MARKETING_NAV_TOP_OFFSET,
  MARKETING_NAV_TRANSPARENT_SHADOW,
} from './marketing-layout'
import { NAV_ITEMS, NAV_MCP_LINK } from './navbar-v2-content'
import { useAuthModalLinkClick } from './use-auth-modal'

/** The bar's primary CTA, the home page's brand pill at navbar size. */
const NAV_CTA = homeButtonClasses({
  variant: 'brand',
  size: 'sm',
  className: 'gap-1',
})

const NAV_TEXT_LINK =
  'rounded-full px-3.5 py-1.5 font-mk-body text-[14px] text-mk-ink-2 leading-5 tracking-[-0.01em] transition-colors duration-quick hover:text-mk-ink focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2'

/** Where the desktop bar (`lg:flex`) takes over from the phone bar. */
const DESKTOP_BAR_QUERY = '(min-width: 1024px)'

const NAV_WORDMARK = '-mt-[5.5px] -mb-[5.5px] -ml-[14px] h-[35px] w-[105px]'

/**
 * The bar's wordmark. The Rive artboard carries margins of its own: this box
 * draws the mark itself at the 70×24 the static logo had, and the negative
 * margins put the mark where that logo sat without growing the row.
 *
 * Both bars are always mounted and one is hidden by CSS, so only the bar on
 * screen plays the animation; the hidden one keeps the still mark and never
 * starts a Rive instance. Both paint the still mark until hydration, so the
 * server's guess at the width never shows.
 */
function NavWordmark({ isAnimated }: { isAnimated: boolean }) {
  return isAnimated ? (
    <BrewAnimatedLogo className={NAV_WORDMARK} />
  ) : (
    <BrewStaticWordmark className={NAV_WORDMARK} />
  )
}

function externalLinkProps(external: boolean | undefined) {
  return external ? { target: '_blank', rel: 'noopener noreferrer' } : {}
}

/** A menu's links for the drawer: its feature first, then each group. */
function menuSections(
  menu: DropdownNavMenu
): Array<{ title?: string; links: ReadonlyArray<DropdownNavLink> }> {
  const [first, ...rest] = menu.groups
  const lead: Array<DropdownNavLink> = menu.feature
    ? [{ label: menu.feature.title, href: menu.feature.href }]
    : []
  return [
    { title: first?.title, links: [...lead, ...(first?.items ?? [])] },
    ...rest.map((group) => ({ title: group.title, links: group.items })),
  ]
}

const DRAWER_ROW =
  'flex items-center gap-1 p-3 text-foreground/80 transition-colors hover:text-foreground'

/**
 * The drawer's nav: plain rows for links, each menu as a native `<details>`
 * (one open at a time via `name`) listing its links, then MCP. It scrolls
 * inside the height-capped sheet, so the header and CTA stay reachable.
 */
function MobileNavList({ onNavigate }: { onNavigate: () => void }) {
  return (
    <ul className="mb-4 flex min-h-0 flex-col divide-y divide-border overflow-y-auto overscroll-contain rounded-xl border border-border text-sm">
      {NAV_ITEMS.map((item) => (
        <li key={item.label}>
          {item.menu ? (
            <details
              name="marketing-mobile-nav"
              className={cn('group', MK_DETAILS_ACCORDION)}
            >
              <summary
                className={cn(
                  DRAWER_ROW,
                  'cursor-pointer list-none justify-between [&::-webkit-details-marker]:hidden'
                )}
              >
                {item.label}
                <ChevronDown
                  aria-hidden="true"
                  className="size-4 opacity-60 transition-transform duration-fast ease-smooth-out group-open:rotate-180"
                />
              </summary>
              <div className="flex flex-col gap-3 px-3 pb-3">
                {menuSections(item.menu).map((section) => (
                  <div
                    key={section.title ?? 'main'}
                    className="flex flex-col gap-0.5"
                  >
                    {section.title ? (
                      <p className="pt-1 text-muted-foreground text-xs">
                        {section.title}
                      </p>
                    ) : null}
                    <ul className="flex flex-col">
                      {section.links.map((link) => (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            onClick={onNavigate}
                            className="flex items-center gap-1.5 py-1.5 text-foreground/80 transition-colors hover:text-foreground"
                            {...externalLinkProps(link.external)}
                          >
                            {link.label}
                            {link.external ? (
                              <ExternalLink className="size-3 opacity-60" />
                            ) : null}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          ) : (
            <Link
              href={item.href}
              onClick={onNavigate}
              className={DRAWER_ROW}
              {...externalLinkProps(item.external)}
            >
              {item.label}
              {item.external ? (
                <ExternalLink className="size-3 opacity-60" />
              ) : null}
            </Link>
          )}
        </li>
      ))}
      <li>
        <Link
          href={NAV_MCP_LINK.href}
          onClick={onNavigate}
          className={DRAWER_ROW}
        >
          {NAV_MCP_LINK.label}
        </Link>
      </li>
    </ul>
  )
}

/**
 * Marketing navbar for `app/(public)/layout.tsx`. At the top of the page the
 * bar is transparent and inset; after scroll it becomes a centered rounded
 * floating island. Entries
 * and menus come from `navbar-v2-content.ts`; the right side is MCP and the
 * home page's brand "Start free" pill (Dashboard once signed in).
 *
 * `/explore/*` routes use a flush opaque bar so the canvas cannot read
 * through the nav.
 */
export function NavbarV2() {
  const [hasScrolled, setHasScrolled] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { isSignedIn } = useAuth()
  const openAuthInPlace = useAuthModalLinkClick()
  const pathname = usePathname()
  const isCanvasRoute = pathname?.startsWith('/explore') ?? false
  const isDesktopBar = useMediaQuery(DESKTOP_BAR_QUERY, true)

  useEffect(() => {
    const syncHasScrolled = () => {
      setHasScrolled(window.scrollY > MARKETING_NAV_SCROLL_THRESHOLD)
    }

    syncHasScrolled()
    window.addEventListener('scroll', syncHasScrolled, { passive: true })
    return () => window.removeEventListener('scroll', syncHasScrolled)
  }, [])

  const toggleDrawer = () => setIsDrawerOpen((prev) => !prev)
  const handleCloseDrawer = () => setIsDrawerOpen(false)

  let headerMaxWidth: number
  if (isCanvasRoute) {
    headerMaxWidth = 9999
  } else if (hasScrolled) {
    headerMaxWidth = MARKETING_NAV_HEADER_MAX_SCROLLED
  } else {
    headerMaxWidth = MARKETING_NAV_HEADER_MAX_TOP
  }

  let navBackground: string
  if (isCanvasRoute) {
    navBackground = MARKETING_NAV_CANVAS_BG
  } else if (hasScrolled) {
    navBackground = MARKETING_NAV_SCROLLED_BG
  } else {
    navBackground = MARKETING_NAV_TOP_BG
  }

  let navShadow: string
  if (isCanvasRoute) {
    navShadow = MARKETING_NAV_CANVAS_SHADOW
  } else if (hasScrolled) {
    navShadow = MARKETING_NAV_SCROLLED_SHADOW
  } else {
    navShadow = MARKETING_NAV_TRANSPARENT_SHADOW
  }

  let mobileMargin: number
  if (isCanvasRoute) {
    mobileMargin = 0
  } else if (hasScrolled) {
    mobileMargin = MARKETING_NAV_MOBILE_MARGIN_SCROLLED
  } else {
    mobileMargin = MARKETING_NAV_MOBILE_MARGIN_TOP
  }

  return (
    <>
      {/*
        Dual-axis sizing, change with care:
          - `animate.maxWidth` shrinks the centered wrapper from
            MARKETING_NAV_HEADER_MAX_TOP (1216px → 1152px pill, the
            max-w-6xl guide-line column) to
            MARKETING_NAV_HEADER_MAX_SCROLLED (1000px) on scroll.
          - `md:px-8` adds a static side gutter on md+ so the pill
            never touches the viewport edge.
          - `style={{ margin: '0 auto' }}` keeps the wrapper centered
            once `maxWidth` becomes the limiting dimension.
        The desktop pill (the `nav` below) is `w-full` and lives
        inside this wrapper, so it adopts the wrapper's animated
        width minus the static padding.
      */}
      <header
        className={cn(
          'fixed right-0 left-0 z-50 flex justify-center',
          'transition-[top,max-width] duration-fast ease-smooth-out',
          !isCanvasRoute && 'md:px-8'
        )}
        style={{
          margin: '0 auto',
          top: isCanvasRoute ? 0 : MARKETING_NAV_TOP_OFFSET,
          maxWidth: headerMaxWidth,
        }}
      >
        <nav
          className={cn(
            'pointer-events-auto relative z-10 hidden h-14 w-full items-center justify-between gap-4 px-5 lg:flex',
            'transition-[background-color,box-shadow] duration-fast ease-smooth-out',
            isCanvasRoute
              ? 'border-border border-b shadow-md'
              : cn(
                  'rounded-full border-[0.5px] backdrop-blur-xl',
                  hasScrolled
                    ? 'border-border dark:border-white/[0.12]'
                    : 'border-border dark:border-white/10'
                )
          )}
          style={{
            backgroundColor: navBackground,
            boxShadow: navShadow,
          }}
        >
          <Link
            href="/"
            className="flex shrink-0 items-center"
            aria-label="Brew home"
          >
            <NavWordmark isAnimated={isDesktopBar} />
          </Link>

          <DropdownNavigation
            items={NAV_ITEMS}
            className="flex-1 justify-center"
          />

          <div className="flex shrink-0 items-center gap-1.5">
            <Link href={NAV_MCP_LINK.href} className={NAV_TEXT_LINK}>
              {NAV_MCP_LINK.label}
            </Link>
            {isSignedIn ? (
              <Link href="/home" className={NAV_CTA}>
                Dashboard <span aria-hidden="true">→</span>
              </Link>
            ) : (
              // A real link to sign-up (it works before hydration and without
              // JS); a plain click then opens the auth modal in place, while
              // a Ctrl/Cmd/middle click keeps the new tab.
              <a href="/sign-up" onClick={openAuthInPlace} className={NAV_CTA}>
                Start free <span aria-hidden="true">→</span>
              </a>
            )}
          </div>
        </nav>

        <div
          className={cn(
            'relative z-10 flex w-full items-center justify-between gap-3 px-4 py-1 lg:hidden',
            'transition-[background-color,box-shadow,margin] duration-fast ease-smooth-out',
            isCanvasRoute
              ? 'border-border border-b shadow-md'
              : cn(
                  'rounded-full border-[0.5px] backdrop-blur-xl',
                  hasScrolled
                    ? 'border-border dark:border-white/[0.12]'
                    : 'border-border dark:border-white/10'
                )
          )}
          style={{
            backgroundColor: navBackground,
            boxShadow: navShadow,
            marginLeft: mobileMargin,
            marginRight: mobileMargin,
          }}
        >
          <Link href="/" className="py-2" aria-label="Brew home">
            <NavWordmark isAnimated={!isDesktopBar} />
          </Link>

          <button
            type="button"
            className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-foreground transition-transform hover:scale-105 active:scale-95"
            onClick={toggleDrawer}
            aria-label={isDrawerOpen ? 'Close menu' : 'Open menu'}
          >
            {isDrawerOpen ? (
              <X className="size-5" />
            ) : (
              <Menu className="size-5" />
            )}
          </button>
        </div>
      </header>

      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          overlayClassName="z-40 backdrop-blur-xs"
          className="bottom-3 z-40 mx-auto max-h-[calc(100dvh-5rem)] w-[95%] rounded-xl border border-border p-4"
        >
          <SheetHeader className="flex-row items-center justify-between p-0 text-muted-foreground">
            <SheetTitle className="font-medium text-muted-foreground text-sm">
              Menu
            </SheetTitle>
            <SheetClose
              aria-label="Close"
              className="cursor-pointer rounded-lg p-1 transition-transform hover:scale-110 active:scale-95"
            >
              <X className="size-5" />
            </SheetClose>
          </SheetHeader>
          <MobileNavList onNavigate={handleCloseDrawer} />

          {isSignedIn ? (
            <Link
              href="/home"
              onClick={handleCloseDrawer}
              className={homeButtonClasses({
                variant: 'brand',
                size: 'md',
                className: 'w-full gap-1',
              })}
            >
              Dashboard <span aria-hidden="true">→</span>
            </Link>
          ) : (
            // The drawer keeps a sign-in path for returning customers.
            <div className="flex gap-2">
              <SignInButton mode="modal">
                <button
                  type="button"
                  onClick={handleCloseDrawer}
                  className={homeButtonClasses({
                    variant: 'outline',
                    size: 'md',
                    className: 'flex-1',
                  })}
                >
                  Sign in
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button
                  type="button"
                  onClick={handleCloseDrawer}
                  className={homeButtonClasses({
                    variant: 'brand',
                    size: 'md',
                    className: 'flex-1 gap-1',
                  })}
                >
                  Start free <span aria-hidden="true">→</span>
                </button>
              </SignUpButton>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}
